import type { ReactNode } from 'react';
import type { EventRow } from '../api/model';
import { compareDecimal, formatDecimal } from '../lib/decimal';
import { eventTypeText, locationText, signedQuantityText, signedQuantityValue, SOURCE_LABEL } from '../lib/labels';
import { formatUtc } from '../lib/time';
import { Link } from '../lib/router';
import { canAssignDeal } from './AssignDealDialog';
import { Table, type Column } from './Table';
import { PriceSourceLabel } from './PriceSource';
import { Dash } from './Val';

const pillClass = (source: EventRow['source']) => `pill${source === 'manual' ? ' warn' : ''}`;

/** The source pill; an event merged with its manual/Binance counterpart (D29) carries both pills. */
export function SourcePills({ e }: { e: EventRow }) {
  return (
    <>
      <span className={pillClass(e.source)}>{SOURCE_LABEL[e.source]}</span>
      {e.merge?.status === 'accepted' ? (
        <>
          {' '}
          <span className={pillClass(e.merge.counterpart_source)} title="Összefésülve a másik forrás rekordjával (D29)">{SOURCE_LABEL[e.merge.counterpart_source]}</span>
        </>
      ) : null}
      {e.merge?.status === 'proposed' ? (
        <>
          {' '}
          <span className="pill" title="Hasonló rekord van a másik forrásban; az összefésülésről még nem döntöttél (D29)">összefésülés javasolt</span>
        </>
      ) : null}
    </>
  );
}

export function PriceCell({ e }: { e: EventRow }) {
  // D49: a borrow's (and a repay's) price LEVEL with where it came from: Candle, Binance spot, or kézi.
  if (e.type === 'borrow' || (e.type === 'repay' && (e.price_level !== null || e.price_level_reason !== null))) {
    if (e.price_level === null) return <Dash reason={e.price_level_reason ?? 'no_candle'} />;
    return (
      <span className="muted">
        {e.price_level_asset ? `${e.price_level_asset} ` : ''}
        {formatDecimal(e.price_level)}
        <PriceSourceLabel source={e.price_level_source} />
      </span>
    );
  }
  if (e.price === null) return <Dash reason="not_applicable" />;
  return <>{formatDecimal(e.price)}</>;
}

function AllocationCell({ e }: { e: EventRow }) {
  if (e.allocations.length === 0) return <span className="muted">nincs</span>;
  return (
    <>
      {e.allocations.map((a, i) => (
        <span key={a.deal_id}>
          {i > 0 ? ', ' : ''}
          {compareDecimal(a.quantity, e.quantity) === 0 ? '100%' : formatDecimal(a.quantity)} · <Link to={`/deals/${a.deal_id}`}>#{a.deal_id}</Link>
        </span>
      ))}
    </>
  );
}

export function EventsTable({
  events,
  variant,
  onVoid,
  onAssign,
  label,
}: {
  events: EventRow[];
  variant: 'recent' | 'deal';
  onVoid?: (e: EventRow) => ReactNode;
  /** Opens the "Ügylethez rendelés" dialog; the button shows only on events that can carry an allocation. */
  onAssign?: (e: EventRow) => void;
  label: string;
}) {
  const cols: Column<EventRow>[] = [
    { key: 'time', label: 'Időpont (UTC)', sortValue: (e) => e.occurred_at, render: (e) => <span className="mono">{formatUtc(e.occurred_at)}</span> },
    { key: 'type', label: 'Típus', sortValue: (e) => eventTypeText(e), render: (e) => eventTypeText(e) },
    { key: 'location', label: 'Helyszín', sortValue: (e) => locationText(e), render: (e) => locationText(e) },
    { key: 'qty', label: variant === 'deal' ? 'Mennyiség' : 'Összeg', numeric: true, sortValue: (e) => signedQuantityValue(e), render: (e) => <span className="mono">{signedQuantityText(e)}</span> },
    { key: 'price', label: 'Ár', numeric: true, help: 'price', ariaLabel: 'ár', sortValue: (e) => (e.type === 'borrow' ? e.price_level : e.price), render: (e) => <span className="mono"><PriceCell e={e} /></span> },
  ];
  if (variant === 'deal') {
    cols.push({ key: 'alloc', label: 'Allokáció', help: 'allocation', ariaLabel: 'allokáció', sortValue: (e) => e.allocations[0]?.deal_id ?? null, render: (e) => <AllocationCell e={e} /> });
  }
  cols.push({ key: 'source', label: 'Forrás', help: 'source', ariaLabel: 'forrás', sortValue: (e) => e.source, render: (e) => <SourcePills e={e} /> });
  if (variant === 'recent') {
    cols.push({
      key: 'deal',
      label: 'Ügylet',
      sortValue: (e) => e.allocations[0]?.deal_id ?? null,
      numeric: true,
      render: (e) =>
        e.allocations.length ? (
          <>
            {e.allocations.map((a, i) => (
              <span key={a.deal_id}>
                {i > 0 ? ', ' : ''}
                <Link to={`/deals/${a.deal_id}`}>#{a.deal_id}</Link>
              </span>
            ))}
          </>
        ) : (
          <span className="muted">—</span>
        ),
    });
  } else {
    cols.push({ key: 'note', label: 'Megjegyzés', render: (e) => e.note ?? '' });
    if (onVoid || onAssign) {
      cols.push({
        key: 'act',
        label: 'Művelet',
        render: (e) => (
          <span className="row-wrap">
            {onAssign && canAssignDeal(e) ? (
              <button type="button" className="btn small" onClick={() => onAssign(e)} aria-label={`Ügylethez rendelés: ${formatUtc(e.occurred_at)}`}>
                Ügylethez rendelés…
              </button>
            ) : null}
            {onVoid ? onVoid(e) : null}
          </span>
        ),
      });
    }
  }
  return <Table columns={cols} rows={events} rowKey={(e) => e.id} defaultSort={{ key: 'time', dir: variant === 'deal' ? 'asc' : 'desc' }} label={label} empty="Még nincs naplózott esemény." />;
}
